import { computed } from 'vue'

import type { ComputedRef, WritableComputedRef } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { AppFormState } from '@shared/ui/fields'
import type { TaskPriority } from '@entities/task'

import { TasksKanbanStage } from '@brighterly/lib-core-types/tasks-kanban/enums'

import { useListsStore } from '@shared/model'

import { useTasksKanbanStore } from './store'

interface TaskGeneralInfoUpdateReturn {
  refStage: WritableComputedRef<TasksKanbanStage | undefined>
  refStageOptions: ComputedRef<AppSelectOption<TasksKanbanStage>[]>
  refPriority: WritableComputedRef<TaskPriority | undefined>
  refPriorityOptions: ComputedRef<AppSelectOption<TaskPriority>[]>
  refIsWaiting: ComputedRef<boolean>
  refIsResolved: ComputedRef<boolean>
  refIsCancelled: ComputedRef<boolean>
  refWaitingReason: WritableComputedRef<string | undefined>
  refWaitingReasonOptions: ComputedRef<AppSelectOption<string>[]>
  refWaitingReasonState: ComputedRef<AppFormState>
  refResolution: WritableComputedRef<string | undefined>
  refResolutionOptions: ComputedRef<AppSelectOption<string>[]>
  refResolutionState: ComputedRef<AppFormState>
  refCustomerOutcome: WritableComputedRef<string | undefined>
  refCustomerOutcomeOptions: ComputedRef<AppSelectOption<string>[]>
  refCustomerOutcomeState: ComputedRef<AppFormState>
  refCancelReason: WritableComputedRef<string | undefined>
  refCancelReasonOptions: ComputedRef<AppSelectOption<string>[]>
  refCancelReasonState: ComputedRef<AppFormState>
}

const toOptions = (labels: Record<string, string> | undefined): AppSelectOption<string>[] =>
  Object.entries(labels ?? {}).map(([value, text]) => ({ value, text }))

export const useTaskGeneralInfoUpdate = (): TaskGeneralInfoUpdateReturn => {
  const tasksKanbanStore = useTasksKanbanStore()
  const listsStore = useListsStore()

  const refLists = computed(() => listsStore.getLists?.tasks_kanban?.lists)

  const refStageOptions = computed<AppSelectOption<TasksKanbanStage>[]>(() =>
    Object.entries(refLists.value?.status ?? {}).map(([value, text]) => ({
      value: value as TasksKanbanStage,
      text,
    })),
  )

  const refStage = computed<TasksKanbanStage | undefined>({
    get: () => tasksKanbanStore.getStage ?? undefined,
    set: (value) => {
      tasksKanbanStore.setStage(value ?? null)
      tasksKanbanStore.clearTransitionFields()
      tasksKanbanStore.setSubmitted(false)
    },
  })

  const refPriorityOptions = computed<AppSelectOption<TaskPriority>[]>(() =>
    Object.entries(refLists.value?.priority ?? {}).map(([value, text]) => ({ value: value as TaskPriority, text })),
  )

  const refPriority = computed<TaskPriority | undefined>({
    get: () => tasksKanbanStore.getPriority ?? undefined,
    set: value => tasksKanbanStore.setPriority(value ?? null),
  })

  const refIsWaiting = computed<boolean>(() => tasksKanbanStore.getStage === TasksKanbanStage.Waiting)
  const refIsResolved = computed<boolean>(() => tasksKanbanStore.getStage === TasksKanbanStage.Resolved)
  const refIsCancelled = computed<boolean>(() => tasksKanbanStore.getStage === TasksKanbanStage.Cancelled)

  const refWaitingReasonOptions = computed<AppSelectOption<string>[]>(() => toOptions(refLists.value?.waiting_reason))
  const refResolutionOptions = computed<AppSelectOption<string>[]>(() => toOptions(refLists.value?.resolution))
  const refCustomerOutcomeOptions = computed<AppSelectOption<string>[]>(() => toOptions(refLists.value?.customer_outcome))
  const refCancelReasonOptions = computed<AppSelectOption<string>[]>(() => toOptions(refLists.value?.cancel_reason))

  const refWaitingReason = computed<string | undefined>({
    get: () => tasksKanbanStore.getWaitingReason ?? undefined,
    set: (value) => tasksKanbanStore.setWaitingReason(value ?? null),
  })

  const refResolution = computed<string | undefined>({
    get: () => tasksKanbanStore.getResolution ?? undefined,
    set: (value) => tasksKanbanStore.setResolution(value ?? null),
  })

  const refCustomerOutcome = computed<string | undefined>({
    get: () => tasksKanbanStore.getCustomerOutcome ?? undefined,
    set: (value) => tasksKanbanStore.setCustomerOutcome(value ?? null),
  })

  const refCancelReason = computed<string | undefined>({
    get: () => tasksKanbanStore.getCancelReason ?? undefined,
    set: (value) => tasksKanbanStore.setCancelReason(value ?? null),
  })

  const errorState = (visible: boolean, value: string | null): AppFormState =>
    tasksKanbanStore.getSubmitted && visible && !value ? 'error' : 'static'

  const refWaitingReasonState = computed<AppFormState>(() =>
    errorState(refIsWaiting.value, tasksKanbanStore.getWaitingReason),
  )
  const refResolutionState = computed<AppFormState>(() =>
    errorState(refIsResolved.value, tasksKanbanStore.getResolution),
  )
  const refCustomerOutcomeState = computed<AppFormState>(() =>
    errorState(refIsResolved.value, tasksKanbanStore.getCustomerOutcome),
  )
  const refCancelReasonState = computed<AppFormState>(() =>
    errorState(refIsCancelled.value, tasksKanbanStore.getCancelReason),
  )

  return {
    refStage,
    refStageOptions,
    refPriority,
    refPriorityOptions,
    refIsWaiting,
    refIsResolved,
    refIsCancelled,
    refWaitingReason,
    refWaitingReasonOptions,
    refWaitingReasonState,
    refResolution,
    refResolutionOptions,
    refResolutionState,
    refCustomerOutcome,
    refCustomerOutcomeOptions,
    refCustomerOutcomeState,
    refCancelReason,
    refCancelReasonOptions,
    refCancelReasonState,
  }
}